import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "@/lib/auth";

export default function Success() {
  const [params] = useSearchParams();
  const ref = params.get("ref");
  const pending = params.get("pending") === "1";
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(true);

  useEffect(() => {
    refresh().finally(() => setRefreshing(false));
  }, [refresh]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="success-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-base">O</div>
            <div className="font-display text-xl">ORIXA</div>
          </Link>
        </div>
      </header>

      <section className="flex-1 grid place-items-center px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-xl text-center">
          <div className="inline-flex h-16 w-16 rounded-full bg-teal-soft text-teal items-center justify-center mb-6">
            <CheckCircle size={32} weight="fill" />
          </div>
          <div className="text-overline text-primary mb-3">{pending ? "Payment received" : "Subscription activated"}</div>
          <h1 className="font-display text-3xl sm:text-5xl tracking-tight leading-[1.05]">
            Your subscription has been<br /><span className="italic text-primary">successfully activated.</span>
          </h1>
          <p className="mt-6 text-base text-muted-foreground leading-relaxed">
            Welcome to Orixa. Your workspace is ready — open your dashboard to brief your first mandate.
          </p>
          {ref && (
            <div className="mt-6 inline-flex items-center gap-2 bg-secondary border border-border rounded-full px-4 py-1.5 text-xs font-mono text-muted-foreground">
              Reference · {ref}
            </div>
          )}

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            {user ? (
              <button onClick={() => navigate("/dashboard")} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3.5 font-semibold card-elev hover:bg-primary/90 transition-colors" data-testid="success-dashboard">
                Open dashboard <ArrowRight weight="bold" />
              </button>
            ) : (
              <Link to="/login" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3.5 font-semibold card-elev hover:bg-primary/90 transition-colors" data-testid="success-login">
                Sign in <ArrowRight weight="bold" />
              </Link>
            )}
            <Link to="/book" className="w-full sm:w-auto border border-border bg-card rounded-lg px-6 py-3.5 font-semibold hover:bg-secondary transition-colors" data-testid="success-book">
              Book onboarding call
            </Link>
          </div>

          <div className="mt-12 pt-8 border-t border-border text-xs text-muted-foreground">
            A receipt has been emailed to you. Reach us at <a href="mailto:support@orixa-invest.co" className="text-primary font-medium">support@orixa-invest.co</a> if you need anything.
            {refreshing && <span className="block mt-2 italic">Refreshing your session…</span>}
          </div>
        </div>
      </section>
    </div>
  );
}
